// The week view (B6, Cal-17): one row per person, one column per day, each
// cell their hours or Day off and how much work that day holds. Any cell or
// day heading opens that day on the timeline — the detail lives there.

import { cn } from "@bednbody/ui/lib/utils";
import { compactSpanLabel, spanLabel } from "./calendar-board";
import { weekdayLabel } from "./calendar-board.labels";
import type { CalendarWeek as CalendarWeekModel } from "./calendar-board.week";
import { BADGE, BadgeImage, StaffAvatar } from "./CalendarIcons";

const NAME_COL = "13rem";

export function CalendarWeek({
  week,
  onOpenDay,
}: {
  week: CalendarWeekModel;
  onOpenDay: (day: string) => void;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div className="min-w-4xl">
        <div className="sticky top-0 z-20 flex border-b border-border/70 bg-card">
          <div
            className="sticky left-0 z-10 flex shrink-0 flex-col justify-center bg-card px-4 py-2"
            style={{ width: NAME_COL }}
          >
            <span className="text-xs font-bold text-muted-foreground">Team</span>
            <span className="text-xs text-muted-foreground">click a day to open it</span>
          </div>
          {week.heads.map((head) => (
            <button
              className={cn(
                "flex min-w-0 flex-1 flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-muted/60",
                head.isToday ? "bg-primary/10" : null,
              )}
              key={head.day}
              onClick={() => onOpenDay(head.day)}
              type="button"
            >
              <span
                className={cn(
                  "text-sm font-bold",
                  head.isToday ? "text-primary-dark" : "text-foreground",
                )}
              >
                {weekdayLabel(head.day)}
                {head.isToday ? " · Today" : ""}
              </span>
              <span className="text-xs text-muted-foreground">
                {head.sessions} session{head.sessions === 1 ? "" : "s"}
              </span>
              {head.unassigned > 0 ? (
                <span className="text-xs font-bold text-destructive">
                  {head.unassigned} need a therapist
                </span>
              ) : null}
            </button>
          ))}
        </div>

        {week.rows.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            Nobody is scheduled this week for these filters.
          </p>
        ) : null}

        {week.rows.map((row) => (
          <div className="flex h-16 border-b border-border/50" key={row.staffId}>
            <div
              className="sticky left-0 z-10 flex shrink-0 items-center gap-2.5 border-r border-border/70 bg-card px-3"
              style={{ width: NAME_COL }}
            >
              <StaffAvatar role={row.role} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground">
                  {row.name}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {row.hubName}
                  {row.role === "rider" ? " · rider" : ""}
                </span>
              </span>
            </div>
            {row.cells.map((cell) => {
              const work = row.role === "rider" ? "trip" : "session";
              return (
                <div
                  className={cn("min-w-0 flex-1 p-1.5", cell.isToday ? "bg-primary/5" : null)}
                  key={cell.day}
                >
                  <button
                    aria-label={`${row.name}, ${weekdayLabel(cell.day)}: ${
                      cell.unscheduled
                        ? "not scheduled"
                        : cell.dayOff
                          ? "day off"
                          : `${cell.shift ? spanLabel(cell.shift) : ""}, ${cell.count} ${work}${cell.count === 1 ? "" : "s"}`
                    }. Open the day.`}
                    className={cn(
                      "flex size-full flex-col items-start justify-center overflow-hidden rounded-lg px-2.5 text-left transition-colors",
                      cell.unscheduled
                        ? "text-muted-foreground hover:bg-muted/60"
                        : cell.dayOff
                          ? "border border-dashed border-border bg-muted text-muted-foreground"
                          : cell.isToday
                            ? "bg-success/10 hover:bg-success/15"
                            : "bg-muted/40 hover:bg-muted",
                    )}
                    onClick={() => onOpenDay(cell.day)}
                    type="button"
                  >
                    {cell.unscheduled ? (
                      <span className="text-xs">—</span>
                    ) : cell.dayOff ? (
                      <span className="flex items-center gap-1.5 text-xs font-bold">
                        <BadgeImage size="xs" src={BADGE.dayOff} />
                        Day off
                      </span>
                    ) : (
                      <>
                        <span className="w-full truncate text-xs font-bold text-foreground">
                          {cell.shift ? compactSpanLabel(cell.shift) : ""}
                        </span>
                        <span className="w-full truncate text-xs font-medium text-success">
                          {cell.count} {work}
                          {cell.count === 1 ? "" : "s"}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
